import React, { FC, useCallback, useEffect, useRef } from 'react';
import { ListingPictureKind } from '@kouto/types';
import usePrevious from 'hooks/usePrevious';
import { useIsMobile } from 'WindowDimensionProvider';
import MinimalVideoPlayer from 'components/MinimalVideoPlayer/MinimalVideoPlayer';
import { listingMediaIsVideo } from 'utils/listings';
import { FormattedListingMedia } from 'types/listings';
import styled from 'styled-components';
import { BrandFlagContainer } from 'components/molecules/BrandFlagContainer';
import MinimalCarousel from './MinimalCarousel';
import { Image } from './styled';

const MediaWrapper = styled.div<{ kind?: ListingPictureKind }>`
  cursor: pointer;
  aspect-ratio: ${({ kind }) =>
    kind === ListingPictureKind.COVER ? '16/9' : '4/3'};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledMinimalVideoPlayer = styled(MinimalVideoPlayer)<{
  isFullScreen?: boolean;
}>`
  & > .play-pause-button,
  & > .mute-button {
    ${({ isFullScreen }) =>
      isFullScreen &&
      `
        background-color: rgba(0, 0, 0, 0.5);
        color: white;
      `}
  }
`;

type Props = {
  setIsFullScreenImageModalOpen?: (isOpen: boolean) => void;
  isFullScreenImageModalOpen?: boolean;
  medias: (FormattedListingMedia & {
    alt?: string;
  })[];
  isRequestToBookEnabled?: boolean;
  selectedMediaIndex: number;
  setSelectedMediaIndex: (index: number) => void;
  brandFlagName?: string | null;
};

const AUTOPLAY_TIMEOUT = 6000; // ms

/**
 * @deprecated Use `brand-embed-plugin/src/components/MediaCarousel/MediaCarousel.tsx` instead.
 */
const MediaCarousel: FC<Props> = ({
  medias,
  setIsFullScreenImageModalOpen,
  isFullScreenImageModalOpen,
  isRequestToBookEnabled,
  selectedMediaIndex,
  setSelectedMediaIndex,
  brandFlagName,
}) => {
  const isMobile = useIsMobile();
  const previousSelectedTabIndex = usePrevious(selectedMediaIndex);
  const timeoutIdRef = useRef<number | null>(null);
  const mediaCount = medias?.length || 0;

  const goToNextSlide = useCallback(() => {
    if (mediaCount > 1) {
      setSelectedMediaIndex((selectedMediaIndex + 1) % mediaCount);
    }
  }, [selectedMediaIndex, mediaCount]);

  const handleClick = () => {
    if (isRequestToBookEnabled) {
      setIsFullScreenImageModalOpen?.(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isRequestToBookEnabled && e.key === 'Enter') {
      setIsFullScreenImageModalOpen?.(true);
    }
  };

  useEffect(() => {
    if (
      mediaCount > 1 &&
      previousSelectedTabIndex !== selectedMediaIndex &&
      !isFullScreenImageModalOpen
    ) {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
      }
      if (medias[selectedMediaIndex]?.type === 'image') {
        timeoutIdRef.current = window.setTimeout(
          goToNextSlide,
          AUTOPLAY_TIMEOUT,
        );
      }
    }

    return () => {
      if (timeoutIdRef.current) {
        window.clearTimeout(timeoutIdRef.current);
      }
    };
  }, [
    medias,
    selectedMediaIndex,
    previousSelectedTabIndex,
    mediaCount,
    goToNextSlide,
    isFullScreenImageModalOpen,
  ]);

  return (
    <BrandFlagContainer brandFlagName={brandFlagName}>
      <MinimalCarousel
        selectedSlideIndex={selectedMediaIndex}
        fit="cover"
        onSlideChange={setSelectedMediaIndex}
        isFullScreenImageModalOpen={isFullScreenImageModalOpen}
      >
        {(medias ?? []).map((media, index) => {
          const isVideo = listingMediaIsVideo(media);
          const content = isVideo ? (
            <StyledMinimalVideoPlayer
              isFullScreen={isFullScreenImageModalOpen}
              key={media.id}
              fit="cover"
              videoUrl={isMobile ? media.urlMobile : media.urlDesktop}
              coverUrl={isMobile ? media.previewMobile : media.previewDesktop}
              play={index === selectedMediaIndex}
              onVideoEnd={goToNextSlide}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              style={{
                cursor: isRequestToBookEnabled ? 'pointer' : 'default',
                height: isFullScreenImageModalOpen ? '75vh' : '100%',
              }}
            />
          ) : (
            <Image
              key={media.id}
              fit={
                media.kind === ListingPictureKind.COVER ? 'cover' : 'contain'
              }
              src={isMobile ? media.urlMobile : media.urlDesktop}
              alt={media.alt}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              style={{
                cursor: isRequestToBookEnabled ? 'pointer' : 'default',
              }}
            />
          );

          if (isFullScreenImageModalOpen) {
            return (
              <MediaWrapper
                key={media.id}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                kind={media.kind}
              >
                {content}
              </MediaWrapper>
            );
          }
          return content;
        })}
      </MinimalCarousel>
    </BrandFlagContainer>
  );
};

export default MediaCarousel;
